/**
 * Route table (H0 finding: the app had no router at all, so every page was dead
 * code).
 *
 * BrowserRouter with paths rather than the hash the previous pages used: a hash
 * cannot be sent to a donor in a link and cannot carry an Open Graph tag, and
 * `03_growth_layer.md` §D.4 makes shareable URLs a hard requirement. Each app
 * origin keeps its own path space; `/api` belongs to the reverse proxy.
 *
 * The pages that carry money are mounted here and nowhere else.
 *
 * ## Why `/3ds-complete` is outside the shell
 *
 * Everything a donor sees mounts inside {@link AppShell}, and the shell boots
 * the session (`AuthProvider` calls `/api/auth/session`, then `/api/me`). The
 * bank's 3-D Secure return lands **inside the challenge frame**, whose parent
 * destroys the frame the moment its message arrives — the session request the
 * shell started inside it dies with it, and devtools shows it as
 * `session (canceled)`: noise on every completed challenge, and one more thing
 * a payment page must never need. Worse, the page's one job is to deliver that
 * message, and a page that waits on a session before rendering it holds the
 * donor's checkout hostage to a login they never needed. So it renders bare:
 * no provider, no session, no API calls, one `postMessage`.
 */

import { AuthProvider } from '@qalam/shared/frontend';
import { useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { createPaymentClient } from './checkout/api';
import { LanguageSwitcher } from './LanguageSwitcher';
import CampaignPage from './pages/CampaignPage';
import CheckoutPage from './pages/CheckoutPage';
import FundraiserPage from './pages/FundraiserPage';
import HomePage from './pages/HomePage';
import PackagesPage from './pages/PackagesPage';
import ThreeDSComplete from './pages/ThreeDSComplete';

/**
 * Every page a donor or an administrator browses: the session provider, the
 * language bar, and the one payment client for the session's lifetime. It holds
 * no state — the cookie and the guest token on each call are the whole of its
 * context.
 */
function AppShell() {
  const client = createPaymentClient();
  const [seats, setSeats] = useState(5);

  return (
    <AuthProvider>
      {/* On every page, not just the checkout: a donor who cannot read the site
          has to be able to change that before they decide to leave. */}
      <div className="lang-bar">
        <LanguageSwitcher />
      </div>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/packages" element={<PackagesPage onChoose={setSeats} />} />
        <Route path="/campaigns/:slug" element={<CampaignPage />} />
        <Route path="/p/:slug" element={<FundraiserPage />} />
        <Route path="/give" element={<CheckoutPage client={client} seats={seats} />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </AuthProvider>
  );
}

/** The route table, without its router, so tests can mount it at a path. */
export function AppRoutes() {
  return (
    <Routes>
      {/* Where the bank's 3-D Secure return redirect lands. It is a real route
          because the redirect happens inside the challenge frame, and the frame
          is the web app's own page by then — see the header for why it is
          deliberately outside the shell. */}
      <Route path="/3ds-complete" element={<ThreeDSComplete />} />
      <Route path="*" element={<AppShell />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
